import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import invoke from 'lodash/invoke';
import { FormattedDate, FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import ScheduleModal from '../../components/ScheduleModal';
import Input from '../../components/Input';
import Button from '../../components/Button';
import Toast, { ToastKind } from '../../components/Toast';
import { timeTrim } from '../VisitRequest/time-helpers';
import vrMessages from '../VisitRequest/messages';
import '../VisitRequest/styles.css';
import REPVISITS_LOCATION_TYPE from '../../utils/repvisits-location-types';

const messages = defineMessages({
  cancel: {
    id: 'reschedule.cancel',
    defaultMessage: 'No, Keep Visit',
  },
  request: {
    id: 'reschedule.confirm',
    defaultMessage: 'Yes, Reschedule',
  },
  prompt: {
    id: 'reschedule.prompt',
    defaultMessage: 'Do you want to reschedule your visit on {firstTimeStr} to {secondTimeStr}?',
  },
  title: {
    id: 'reschedule.title',
    defaultMessage: 'You already have a visit with {school}.',
  },
  error: {
    id: 'reschedule.notifications.error',
    defaultMessage: 'Sorry, this appointment is no longer available. Please select another appointment.',
  },
  approvedTitle: {
    id: 'reschedule.notifications.approvedTitle',
    defaultMessage: 'Reschedule confirmed!',
  },
  approved: {
    id: 'reschedule.notifications.approved',
    defaultMessage: 'Your request has been automatically confirmed by the high school.',
  },
  pendingTitle: {
    id: 'reschedule.notifications.pendingTitle',
    defaultMessage: 'Reschedule requested!',
  },
  pending: {
    id: 'reschedule.notifications.pending',
    defaultMessage: 'You will receive an email notification when your request has been confirmed.',
  },
});

export class VisitRequest extends React.PureComponent {
  static propTypes = {
    cancelAppointmentDay: PropTypes.shape({
      schoolTimezone: PropTypes.string,
      id: PropTypes.string,
      date: PropTypes.string,
      appointments: PropTypes.array,
    }),
    cancelAppointmentId: PropTypes.string,
    bookAppointmentDay: PropTypes.shape({
      id: PropTypes.string,
      date: PropTypes.string,
      appointments: PropTypes.array,
    }),
    bookAppointmentId: PropTypes.string,
    onClose: PropTypes.func,
    mountNode: PropTypes.any,
    onOpen: PropTypes.func,
    open: PropTypes.bool,
    onConfirm: PropTypes.func,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    rescheduleRsvpRepVisit: PropTypes.func.isRequired,
  };

  state = {
    visitUrlError: false,
    visitUrl: '',
    selectedInPerson: false,
  };

  newNotification = (message, kind, title='') => <Toast active title={title} description={message} kind={kind} />;

  onVisitUrlChange = (event) => {
    const visitUrl = event.target.value;
    this.setState({ visitUrl, visitUrlError: !visitUrl });
  }

  selectInPerson = () => this.setState({ selectedInPerson: true });

  selectVirtual = () => this.setState({ selectedInPerson: false });

  handleVisitUrl = () => {
    const { bookAppointmentDay, bookAppointmentId } = this.props;
    const { visitUrl } = this.state;
    const appointmentNode = get(bookAppointmentDay, 'appointments', [])
      .find((appointment) => (appointment.id === bookAppointmentId));
    const visitLocationType = get(appointmentNode, 'visitLocationType');
    const eitherWithVirtual = visitLocationType === REPVISITS_LOCATION_TYPE.EITHER && !this.state.selectedInPerson;
    if (!visitUrl && (visitLocationType === REPVISITS_LOCATION_TYPE.VIRTUAL || eitherWithVirtual)) {
      this.setState({ visitUrlError: true });
      return false;
    }
    return true;
  }

  reschedule = () => {
    const { rescheduleRsvpRepVisit, bookAppointmentDay, bookAppointmentId, cancelAppointmentDay, cancelAppointmentId } = this.props;
    const { visitUrl, selectedInPerson } = this.state;

    if (!this.handleVisitUrl()) return;

    const onFailure = () => {
      invoke(this.props, 'onConfirm', this.newNotification(this.props.intl.formatMessage(messages.error), ToastKind.error));
      this.closeAndResetValues();
    };

    const onSuccess = (transaction) => {
      const booked = get(transaction, 'data.rescheduleRsvpRepVisit.bookedRepVisit.appointments').find(
        (appointment) => (appointment.id === this.props.bookAppointmentId),
      );

      const status = get(booked, 'userBookingStatus');
      if (status === 'APPROVED') {
        invoke(this.props, 'onConfirm',
          this.newNotification(
            this.props.intl.formatMessage(messages.approved),
            ToastKind.success,
            this.props.intl.formatMessage(messages.approvedTitle),
          ));
      } else if (status === 'PENDING') {
        invoke(this.props, 'onConfirm',
          this.newNotification(
            this.props.intl.formatMessage(messages.pending),
            ToastKind.success,
            this.props.intl.formatMessage(messages.pendingTitle),
          ));
      }
      this.closeAndResetValues();
    };

    const appointmentNode = get(bookAppointmentDay, 'appointments', [])
      .find((appointment) => (appointment.id === bookAppointmentId));
    let chosenVisitLocationType = get(appointmentNode, 'visitLocationType') || REPVISITS_LOCATION_TYPE.IN_PERSON;
    if (chosenVisitLocationType === REPVISITS_LOCATION_TYPE.EITHER) {
      chosenVisitLocationType = selectedInPerson ? REPVISITS_LOCATION_TYPE.IN_PERSON : REPVISITS_LOCATION_TYPE.VIRTUAL;
    }

    rescheduleRsvpRepVisit({
      variables: {
        input: {
          repVisit: {
            dayId: bookAppointmentDay.id,
            appointmentId: bookAppointmentId,
            visitUrl,
            chosenVisitLocationType,
          },
          cancelRepVisit: {
            dayId: cancelAppointmentDay.id,
            appointmentId: cancelAppointmentId,
          },
        },
      },
    }).then(onSuccess, onFailure);
  };

  closeAndResetValues = () => {
    const { onClose } = this.props;
    onClose();
    this.setState({ visitUrl: '' });
  };

  modalIsInValid = (visitLocationType) => {
    if (!visitLocationType) {
      return false;
    }
    const { selectedInPerson, visitUrl } = this.state;
    return REPVISITS_LOCATION_TYPE.IN_PERSON !== visitLocationType && !selectedInPerson && !visitUrl;
  }

  renderHeader = (cancelDate, cancelTimeStr, bookDate, bookTimeStr) => (
    <div styleName="prompt" data-cy="schedule-prompt">
      <FormattedMessage
        {...messages.prompt}
        values={{
          firstTimeStr: (
            <b>
              <FormattedDate
                value={`${cancelDate}T12:00:00`}
                weekday="long"
                year="numeric"
                month="long"
                day="numeric"
              />
              {` on ${cancelTimeStr}`}
            </b>
          ),
          secondTimeStr: (
            <b>
              <FormattedDate
                value={`${bookDate}T12:00:00`}
                weekday="long"
                year="numeric"
                month="long"
                day="numeric"
              />
              {` from ${bookTimeStr}`}
            </b>
          ),
        }}
      />
    </div>
  );

  renderVisitUrlInput = (intl, visitLocationType, virtual) => {
    if (virtual && visitLocationType !== REPVISITS_LOCATION_TYPE.VIRTUAL) return null;
    if (!virtual && visitLocationType !== REPVISITS_LOCATION_TYPE.EITHER) return null;
    if (!virtual && this.state.selectedInPerson) return null;
    const virtualStyle = virtual ? ' space-top' : '';
    return (
      <div data-cy="visit-url-input-container" styleName={`visit-url-container ${virtualStyle}`}>
        {intl.formatMessage(vrMessages.virtualVisitHint)}
        <Input
          data-cy="visit-url-input"
          styleName="visit-url-input"
          value={this.state.visitUrl}
          error={this.state.visitUrlError}
          onChange={this.onVisitUrlChange}
        />
      </div>
    );
  }

  renderVisitLocationTypeSelector = (visitLocationType) => {
    if (visitLocationType !== REPVISITS_LOCATION_TYPE.EITHER) return null;
    return (
      <Button.Group fluid>
        <Button
          data-cy="select-in-person-button"
          onClick={this.selectInPerson}
          styleName={!this.state.selectedInPerson ? 'either-location-type' : 'either-location-type-selected'}
        >
          <FormattedMessage {...vrMessages.visitInPerson} />
        </Button>
        <Button
          data-cy="select-virtual-button"
          onClick={this.selectVirtual}
          styleName={this.state.selectedInPerson ? 'either-location-type' : 'either-location-type-selected'}
        >
          <FormattedMessage {...vrMessages.visitVirtual} />
        </Button>
      </Button.Group>
    );
  }

  renderVirtualScheduler = (intl, visitLocationType, cancelDate, cancelTimeStr, bookDate, bookTimeStr) => (
    <React.Fragment>
      {this.renderHeader(cancelDate, cancelTimeStr, bookDate, bookTimeStr)}
      {this.renderVisitUrlInput(intl, visitLocationType, true)}
      {this.renderVisitLocationTypeSelector(visitLocationType)}
      {this.renderVisitUrlInput(intl, visitLocationType, false)}
    </React.Fragment>
  )

  render() {
    const { onClose, cancelAppointmentDay, cancelAppointmentId, bookAppointmentDay, bookAppointmentId,
      mountNode, onOpen, open, intl } = this.props;
    const cancelAppointmentNode = get(cancelAppointmentDay, 'appointments', [])
      .find((appointment) => (appointment.id === cancelAppointmentId));
    const bookAppointmentNode = get(bookAppointmentDay, 'appointments', [])
      .find((appointment) => (appointment.id === bookAppointmentId));
    const highSchoolName = get(bookAppointmentDay, 'highSchool.name', 'Unknown School');
    const cancelDate = get(cancelAppointmentDay, 'date');
    const bookDate = get(bookAppointmentDay, 'date');
    const cancelStartTime = get(cancelAppointmentNode, 'startTime');
    const cancelEndTime = get(cancelAppointmentNode, 'endTime');
    const bookStartTime = get(bookAppointmentNode, 'startTime');
    const bookEndTime = get(bookAppointmentNode, 'endTime');
    const visitLocationType = get(bookAppointmentNode, 'visitLocationType');
    const cancelTimeStr = timeTrim(cancelStartTime, cancelEndTime);
    const bookTimeStr = timeTrim(bookStartTime, bookEndTime);
    const props = { onClose, mountNode, onOpen, open };

    return (
      <div>
        {onClose !== undefined && (
        <ScheduleModal
          title={intl.formatMessage(messages.title, { school: highSchoolName })}
          message={(
            <div>
              {this.renderVirtualScheduler(intl, visitLocationType, cancelDate, cancelTimeStr, bookDate, bookTimeStr)}
            </div>
          )}
          cancelText={intl.formatMessage(messages.cancel)}
          confirmText={intl.formatMessage(messages.request)}
          onCancel={this.closeAndResetValues}
          closeAndResetValues={this.closeAndResetValues}
          onConfirm={this.reschedule}
          {...props}
          isInvalid={this.modalIsInValid(visitLocationType)}
        />
        )}
      </div>
    );
  }
}

export default injectIntl(VisitRequest);
